import init, {
  DCCTConstants,
  parseDCCTFooter,
  decompressChunk as decompressChunkRaw,
  decompressChunkInto as decompressChunkIntoRaw,
  validateDICOMMetadataStack,
} from './wasm/pkg/dcmctkit_wasm.js';

/**
 * Decodes one DCCT chunk into an existing voxel array.
 *
 * @param {Uint8Array} compressedData Encoded DCCT chunk bytes.
 * @param {{ x: number, y: number, z: number }} shape Must match the chunk descriptor from {@link parseDCCTFooter}.
 * @param {readonly [number, number]} version `[major, minor]` DCCT version from the file's own
 *   trailer (see {@link DCCTConstants.versionSize}) — controls whether the chunk needs
 *   byte-unshuffling after decompression. Do not hardcode {@link DCCTConstants.version} here, or
 *   files older than the current max supported version will silently mis-decode.
 * @param {Int16Array} output Destination voxel array whose length must equal the chunk voxel count.
 * @returns {void}
 *
 * Throws when the output size is wrong, the shape is invalid, the version is unsupported, or
 * decoding fails.
 */
export const decompressChunkInto = (compressedData, shape, version, output) => {
  decompressChunkIntoRaw(
    compressedData,
    shape.x,
    shape.y,
    shape.z,
    version[0],
    version[1],
    output,
  );
};

/**
 * Decodes one DCCT chunk into a newly allocated voxel array.
 *
 * @param {Uint8Array} compressedData Encoded DCCT chunk bytes.
 * @param {{ x: number, y: number, z: number }} shape Must match the chunk descriptor from {@link parseDCCTFooter}.
 * @param {readonly [number, number]} version `[major, minor]` DCCT version — see
 *   {@link decompressChunkInto}.
 * @returns {Int16Array}
 *
 * Throws when the shape is empty, overflows, the version is unsupported, or the decoded byte
 * count does not match.
 */
export const decompressChunk = (compressedData, shape, version) => {
  return decompressChunkRaw(compressedData, shape.x, shape.y, shape.z, version[0], version[1]);
};

// `parseDCCTFooter` and `validateDICOMMetadataStack` need no JS wrapping — they take
// the same arguments the wasm exports expect, so they are re-exported directly. Their
// types and docs live in `index.d.ts`.
export { DCCTConstants, parseDCCTFooter, validateDICOMMetadataStack };

/** Initializes the WebAssembly module generated by wasm-pack. */
export default init;
